import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { PageHero } from '../components/PageHero';
import { ProtectedEmail } from '../components/ProtectedEmail';
import { AntiBot } from '../components/AntiBot';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const { t, language } = useLanguage();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [antiBotValid, setAntiBotValid] = useState(false);
  const [formLoadTime] = useState(Date.now());
  const [captchaData, setCaptchaData] = useState({ answer: null, expected: null });
  const [siteImages, setSiteImages] = useState({ team: '', page_heroes: {} });
  const [aboutContent, setAboutContent] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [imagesRes, aboutRes, contactRes] = await Promise.all([
          axios.get(`${API}/site-images`),
          axios.get(`${API}/about-content`),
          axios.get(`${API}/contact-info`)
        ]);
        setSiteImages(imagesRes.data);
        setAboutContent(aboutRes.data);
        setContactInfo(contactRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Helper to get about content by language
  const getAboutText = (field) => {
    if (!aboutContent) return '';
    const key = `${field}_${language}`;
    return aboutContent[key] || aboutContent[`${field}_it`] || '';
  };

  // Helper to get contact info by language
  const getContactText = (field) => {
    if (!contactInfo) return '';
    const key = `${field}_${language}`;
    return contactInfo[key] || contactInfo[`${field}_it`] || contactInfo[field] || '';
  };

  // Anti-bot callback
  const handleAntiBotChange = useCallback((isValid, data) => {
    setAntiBotValid(isValid);
    if (data) {
      setCaptchaData(data);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!antiBotValid) {
      toast.error(language === 'it' ? 'Completa la verifica di sicurezza' : 
                  language === 'de' ? 'Bitte Sicherheitsprüfung abschließen' :
                  language === 'fr' ? 'Veuillez compléter la vérification de sécurité' :
                  'Please complete the security check');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        preferred_language: language,
        // Anti-bot data
        honeypot: '',
        formLoadTime: formLoadTime,
        captchaAnswer: captchaData.answer,
        captchaExpected: captchaData.expected
      });
      toast.success(t('contact.success'));
      setForm({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('contact.error');
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero */}
      <PageHero
        title={t('contact.title')}
        subtitle={t('contact.subtitle')}
        backgroundUrl={siteImages.page_heroes?.contact}
        darkMode={true}
      />

      {/* Chi Siamo / Team Section */}
      <section data-testid="team-section" className="py-16 lg:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Team Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#F5F5F7]">
                {siteImages.team ? (
                  <img
                    src={siteImages.team}
                    alt="Il nostro team"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Users className="w-24 h-24 text-[#E5E5EA]" />
                  </div>
                )}
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#FF3B30] hidden lg:block" />
            </motion.div>

            {/* Team Description */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0B1221] mb-6">
                {getAboutText('title') || (language === 'it' ? 'Chi Siamo' : language === 'de' ? 'Über Uns' : language === 'fr' ? 'Qui Sommes-Nous' : 'About Us')}
              </h2>
              <p className="text-[#6E6E73] text-lg leading-relaxed mb-6">
                {getAboutText('paragraph1') || (language === 'it' 
                  ? 'Dal 1977, Cantiere Nautico Brusa è sinonimo di eccellenza nel settore nautico sul Lago Maggiore. La nostra passione per il mare e la dedizione al servizio clienti ci hanno permesso di crescere e diventare un punto di riferimento per gli amanti della nautica.'
                  : language === 'de'
                  ? 'Seit 1977 steht Cantiere Nautico Brusa für Exzellenz in der Nautikbranche am Lago Maggiore. Unsere Leidenschaft für das Meer und unser Engagement für den Kundenservice haben es uns ermöglicht, zu wachsen und zu einer Referenz für Nautikliebhaber zu werden.'
                  : language === 'fr'
                  ? 'Depuis 1977, Cantiere Nautico Brusa est synonyme d\'excellence dans le secteur nautique sur le lac Majeur. Notre passion pour la mer et notre dévouement au service client nous ont permis de grandir et de devenir une référence pour les amateurs de nautisme.'
                  : 'Since 1977, Cantiere Nautico Brusa has been synonymous with excellence in the nautical sector on Lake Maggiore. Our passion for the sea and dedication to customer service have allowed us to grow and become a reference point for nautical enthusiasts.')}
              </p>
              <p className="text-[#6E6E73] text-lg leading-relaxed mb-8">
                {getAboutText('paragraph2') || (language === 'it'
                  ? 'Il nostro team di specialisti certificati offre competenza e professionalità in ogni aspetto della nautica: dalla vendita alla manutenzione, dal trasporto all\'invernaggio. Siamo qui per accompagnarvi in ogni fase della vostra esperienza nautica.'
                  : language === 'de'
                  ? 'Unser Team zertifizierter Spezialisten bietet Kompetenz und Professionalität in allen Aspekten der Nautik: vom Verkauf über die Wartung bis hin zum Transport und der Winterlagerung. Wir sind hier, um Sie in jeder Phase Ihrer nautischen Erfahrung zu begleiten.'
                  : language === 'fr'
                  ? 'Notre équipe de spécialistes certifiés offre compétence et professionnalisme dans tous les aspects du nautisme : de la vente à l\'entretien, du transport à l\'hivernage. Nous sommes là pour vous accompagner à chaque étape de votre expérience nautique.'
                  : 'Our team of certified specialists offers expertise and professionalism in every aspect of boating: from sales to maintenance, from transport to winter storage. We are here to accompany you at every stage of your nautical experience.')}
              </p>
              <div className="flex flex-wrap gap-8">
                <div>
                  <div className="text-3xl font-bold text-[#0B1221]">{aboutContent?.stat1_value || '47+'}</div>
                  <div className="text-sm text-[#6E6E73] uppercase tracking-wider">
                    {getAboutText('stat1_label') || (language === 'it' ? 'Anni di esperienza' : language === 'de' ? 'Jahre Erfahrung' : language === 'fr' ? 'Années d\'expérience' : 'Years of experience')}
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#0B1221]">{aboutContent?.stat2_value || '12'}</div>
                  <div className="text-sm text-[#6E6E73] uppercase tracking-wider">
                    {getAboutText('stat2_label') || (language === 'it' ? 'Specialisti' : language === 'de' ? 'Spezialisten' : language === 'fr' ? 'Spécialistes' : 'Specialists')}
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#FF3B30]">{aboutContent?.stat3_value || '100%'}</div>
                  <div className="text-sm text-[#6E6E73] uppercase tracking-wider">
                    {getAboutText('stat3_label') || (language === 'it' ? 'Passione' : language === 'de' ? 'Leidenschaft' : language === 'fr' ? 'Passion' : 'Passion')}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-2xl font-bold text-[#0B1221] mb-8">
                {contactInfo?.company_name || 'Cantiere Nautico Brusa'}
              </h2>

              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5F5F7] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#FF3B30]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0B1221] mb-1">{t('contact.address')}</p>
                    <p className="text-[#6E6E73]">
                      {contactInfo?.address_line1 || 'Via Pizzante 11'}<br />
                      {contactInfo?.address_line2 || '6595 Riazzino (Locarno)'}<br />
                      {contactInfo?.address_line3 || 'Svizzera'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5F5F7] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#FF3B30]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0B1221] mb-1">{t('contact.phone')}</p>
                    <a 
                      href={`tel:${(contactInfo?.phone || '+41 91 792 19 46').replace(/\s/g, '')}`}
                      data-testid="contact-phone-link"
                      className="text-[#6E6E73] hover:text-[#FF3B30] transition-colors"
                    >
                      {contactInfo?.phone || '+41 91 792 19 46'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5F5F7] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#FF3B30]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0B1221] mb-1">{t('contact.email')}</p>
                    <ProtectedEmail 
                      user={contactInfo?.email_user || 'info'}
                      domain={contactInfo?.email_domain || 'nauticabrusa.ch'}
                      testId="contact-email-link"
                      className="text-[#6E6E73] hover:text-[#FF3B30] transition-colors"
                    />
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5F5F7] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#FF3B30]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0B1221] mb-1">{t('contact.openHours')}</p>
                    <p className="text-[#6E6E73]">
                      {getContactText('hours_weekday') || t('contact.weekdays')}<br />
                      {getContactText('hours_saturday') || t('contact.saturday')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="aspect-[16/9] bg-[#F5F5F7] overflow-hidden">
                <iframe
                  data-testid="contact-map"
                  src={contactInfo?.map_embed_url || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2769.516789456789!2d8.789!3d46.179!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4785c81234567890%3A0x1234567890abcdef!2sVia%20Pizzante%2011%2C%206595%20Riazzino%2C%20Switzerland!5e0!3m2!1sen!2sch!4v1234567890"}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Cantiere Nautico Brusa Location"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="bg-[#F5F5F7] p-8 lg:p-12">
                <h2 className="text-2xl font-bold text-[#0B1221] mb-2">
                  {getContactText('form_title') || t('contact.send')}
                </h2>
                <p className="text-[#6E6E73] mb-8">
                  {getContactText('form_subtitle') || t('contact.subtitle')}
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      data-testid="contact-name"
                      placeholder={t('contact.name')}
                      value={form.name}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      required
                      className="bg-white border-none py-4"
                    />
                    <Input
                      data-testid="contact-email"
                      type="email"
                      placeholder={t('contact.email')}
                      value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      required
                      className="bg-white border-none py-4"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      data-testid="contact-phone"
                      type="tel"
                      placeholder={t('contact.phone')}
                      value={form.phone}
                      onChange={(e) => setForm({...form, phone: e.target.value})}
                      className="bg-white border-none py-4"
                    />
                    <Input
                      data-testid="contact-subject"
                      placeholder={t('contact.subject')}
                      value={form.subject}
                      onChange={(e) => setForm({...form, subject: e.target.value})}
                      required
                      className="bg-white border-none py-4"
                    />
                  </div>

                  <Textarea
                    data-testid="contact-message"
                    placeholder={t('contact.message')}
                    value={form.message}
                    onChange={(e) => setForm({...form, message: e.target.value})}
                    required
                    className="bg-white border-none min-h-[150px]"
                  />

                  {/* Anti-Bot Verification */}
                  <AntiBot 
                    language={language} 
                    onValidChange={handleAntiBotChange}
                  />

                  <Button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={submitting || !antiBotValid}
                    className="w-full bg-[#0B1221] text-white hover:bg-[#1A2639] rounded-full py-4 text-sm font-medium disabled:opacity-50"
                  >
                    {submitting ? '...' : (
                      <>
                        {t('contact.send')}
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
